import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { IdeaCacheProvider } from "@/contexts/IdeaCacheContext";
import Navbar from "./components/Navbar";

import LandingPage from "./pages/LandingPage";
import StartupDetail from "./pages/StartupDetail";
import StartupTrendDetail from "./pages/StartupTrendDetail";
import StartupProfile from "./pages/StartupProfile";
import StartupDetailView from "./pages/StartupDetailView";
import StartupExplorer from "./pages/StartupExplorer";
import TrendBrowser from "./pages/TrendBrowser";
import Dashboard from "./pages/Dashboard";
import IdeaDetail from "./pages/IdeaDetail";
import IdeaBuilder from "./pages/IdeaBuilder";
import Mentor from "./pages/Mentor";
import Settings from "./pages/Settings";
import Pricing from "./pages/Pricing";
import Account from "./pages/Account";
import AutomationLogs from "./pages/AutomationLogs";
import Unsubscribe from "./pages/Unsubscribe";
import NotFound from "./pages/NotFound";
import EnvTest from "./pages/EnvTest";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import Profile from "./pages/Profile";
import PublicIdea from "./pages/PublicIdea";
import DeckViewer from "./pages/DeckViewer";
import PublicDeckViewer from "./pages/PublicDeckViewer";
import Metrics from "./pages/Metrics";
import DayOneAnalytics from "./pages/DayOneAnalytics";
import BuildWorkspace from "./pages/BuildWorkspace";
import CodeWorkspace from "./pages/CodeWorkspace";
import Create from "./pages/Create";
import PlanModePage from "./pages/PlanModePage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { CookieBanner } from "./components/CookieBanner";
import { BetaBanner } from "./components/BetaBanner";

import Admin from "./pages/Admin";
import AdminMetrics from "./pages/AdminMetrics";
import SavedIdeas from "./pages/SavedIdeas";
import MyApps from "./pages/MyApps";
import Test from "./pages/Test";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";

// builder internals
import PreviewPage from "./pages/__preview";
import SandboxPage from "./pages/__sandbox";
import RenderPage from "./pages/__render";
import SandboxEntry from "./pages/__sandbox_entry";
import SandboxFile from "./pages/__sandbox_file";

const queryClient = new QueryClient();

function RoutedApp() {
  const location = useLocation();
  const hideNavbar = location.pathname.startsWith("/__");

  return (
    <>
      {!hideNavbar && <Navbar />}
      {!hideNavbar && <BetaBanner />}

      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/startups" element={<StartupExplorer />} />
        <Route path="/discover" element={<StartupExplorer />} />
        <Route path="/ideas" element={<StartupExplorer />} />
        <Route path="/startups/:id" element={<StartupDetailView />} />
        <Route path="/build/:ideaId" element={<BuildWorkspace />} />
        <Route path="/startup/:id" element={<StartupDetail />} />
        <Route path="/startup-trend/:id" element={<StartupTrendDetail />} />
        <Route path="/startup/:slug" element={<StartupProfile />} />
        <Route path="/i/:slug" element={<PublicIdea />} />
        <Route path="/deck/public/:token" element={<PublicDeckViewer />} />
        <Route path="/signin" element={<Auth />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/traction" element={<Metrics />} />
        <Route path="/metrics" element={<DayOneAnalytics />} />
        <Route path="/idea/:id" element={<IdeaDetail />} />
        <Route path="/idea/:ideaId/build" element={<IdeaBuilder />} />
        <Route path="/deck/:id" element={<DeckViewer />} />
        <Route path="/trends" element={<TrendBrowser />} />
        <Route path="/mentor" element={<Mentor />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/account" element={<Account />} />
        <Route path="/automation-logs" element={<AutomationLogs />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/create" element={<ErrorBoundary><Create /></ErrorBoundary>} />
        <Route path="/plan/:projectId" element={<ErrorBoundary><PlanModePage /></ErrorBoundary>} />
        <Route path="/code-workspace" element={<CodeWorkspace />} />
        <Route path="/saved" element={<SavedIdeas />} />
        <Route path="/my-apps" element={<MyApps />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/metrics" element={<AdminMetrics />} />
        <Route path="/test" element={<Test />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/env-test" element={<EnvTest />} />

        {/* internal */}
        <Route path="/__sandbox" element={<SandboxPage />} />
        <Route path="/__render" element={<RenderPage />} />
        <Route path="/__preview" element={<PreviewPage />} />
        <Route path="/__sandbox_entry" element={<SandboxEntry />} />
        <Route path="/__sandbox_file/:sessionId/*" element={<SandboxFile />} />

        <Route path="*" element={<NotFound />} />
      </Routes>

      {!hideNavbar && <CookieBanner />}
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <IdeaCacheProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <RoutedApp />
              </BrowserRouter>
            </TooltipProvider>
          </IdeaCacheProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export { App };
